/** ClientFormModal — form modal for creating or editing a client. */

import { useState, useEffect, type FormEvent } from "react";
import { Modal, Input, Select, message } from "antd";
import { useCreateClient, useUpdateClient } from "../../api/queries";
import type { Client } from "../../types";
import styles from "../../styles/client.module.css";
import badgeStyles from "../../styles/badge.module.css";

const INDUSTRY_OPTIONS = [
  { value: "金融", label: "金融" },
  { value: "科技", label: "科技" },
  { value: "房地产", label: "房地产" },
  { value: "制造业", label: "制造业" },
  { value: "医药", label: "医药" },
  { value: "教育", label: "教育" },
  { value: "能源", label: "能源" },
  { value: "传媒", label: "传媒" },
  { value: "零售", label: "零售" },
  { value: "其他", label: "其他" },
];

const CLIENT_TYPE_OPTIONS = [
  { value: "enterprise", label: "企业" },
  { value: "individual", label: "个人" },
];

interface ClientForm {
  name: string;
  type: Client["type"];
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  industry: string;
  creditCode: string;
  address: string;
  notes: string;
}

const INITIAL_FORM: ClientForm = {
  name: "",
  type: "enterprise",
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  industry: "",
  creditCode: "",
  address: "",
  notes: "",
};

interface ClientFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Client to edit - if provided, modal is in edit mode */
  client?: Client;
  /** 新建成功后回调，携带刚创建的客户。调用方据此把它选中，
   * 免去「建完再自己去下拉里找一遍」。编辑模式不触发。 */
  onCreated?: (created: Client) => void;
}

export default function ClientFormModal({
  open,
  onClose,
  client,
  onCreated,
}: ClientFormModalProps) {
  const isEdit = !!client;
  const [form, setForm] = useState<ClientForm>({ ...INITIAL_FORM });
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();
  const [messageApi, contextHolder] = message.useMessage();

  // Prefill form when editing
  useEffect(() => {
    if (client) {
      setForm({
        name: client.name,
        type: client.type,
        contactName: client.contactName ?? "",
        contactPhone: client.contactPhone ?? "",
        contactEmail: client.contactEmail ?? "",
        industry: client.industry ?? "",
        creditCode: client.creditCode ?? "",
        address: client.address ?? "",
        notes: client.notes ?? "",
      });
    } else {
      setForm({ ...INITIAL_FORM });
    }
  }, [client, open]);

  const updateField = <K extends keyof ClientForm>(
    key: K,
    value: ClientForm[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) {
      messageApi.warning("请输入客户名称");
      return;
    }

    if (form.creditCode && form.creditCode.length !== 18) {
      messageApi.warning("统一社会信用代码应为18位");
      return;
    }

    if (form.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail)) {
      messageApi.warning("请输入有效的邮箱地址");
      return;
    }

    try {
      if (isEdit && client) {
        await updateClient.mutateAsync({
          id: client.id,
          data: {
            name: form.name.trim(),
            type: form.type,
            contactName: form.contactName.trim() || null,
            contactPhone: form.contactPhone.trim() || null,
            contactEmail: form.contactEmail.trim() || null,
            industry: form.industry || null,
            creditCode: form.creditCode.trim() || null,
            address: form.address.trim() || null,
            notes: form.notes.trim() || null,
          },
        });
        messageApi.success("客户更新成功");
      } else {
        const res = await createClient.mutateAsync({
          name: form.name.trim(),
          type: form.type,
          contactName: form.contactName.trim() || null,
          contactPhone: form.contactPhone.trim() || null,
          contactEmail: form.contactEmail.trim() || null,
          industry: form.industry || null,
          creditCode: form.creditCode.trim() || null,
          address: form.address.trim() || null,
          notes: form.notes.trim() || null,
        });
        messageApi.success("客户创建成功");
        if (res?.data) onCreated?.(res.data);
      }
      setForm({ ...INITIAL_FORM });
      onClose();
    } catch {
      messageApi.error(isEdit ? "更新失败，请稍后重试" : "创建失败，请稍后重试");
    }
  };

  const handleClose = () => {
    if (!createClient.isPending && !updateClient.isPending) {
      setForm({ ...INITIAL_FORM });
      onClose();
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title={isEdit ? "编辑客户" : "新建客户"}
        open={open}
        onCancel={handleClose}
        onOk={handleSubmit}
        okText="确认"
        cancelText="取消"
        confirmLoading={createClient.isPending || updateClient.isPending}
        okButtonProps={{ className: badgeStyles.modalOkBtn }}
        cancelButtonProps={{ className: badgeStyles.modalCancelBtn }}
        width={560}
        rootClassName="jerryClientFormModal"
        destroyOnHidden
      >
        <form onSubmit={handleSubmit}>
          <div className={styles.formRow}>
            {/* Name — full width */}
            <div className={styles.formGroupFull}>
              <label className={styles.formLabelRequired}>客户名称</label>
              <Input
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="如：某某科技有限公司"
                maxLength={100}
                className={badgeStyles.modalInput}
              />
            </div>

            {/* Type */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>客户类型</label>
              <Select
                value={form.type}
                onChange={(v) => updateField("type", v)}
                options={CLIENT_TYPE_OPTIONS}
              />
            </div>

            {/* Industry */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>所属行业</label>
              <Select
                value={form.industry || undefined}
                onChange={(v) => updateField("industry", v ?? "")}
                options={INDUSTRY_OPTIONS}
                placeholder="选择行业"
                allowClear
              />
            </div>

            {/* Credit Code — enterprise only */}
            {form.type === "enterprise" && (
              <div className={styles.formGroupFull}>
                <label className={styles.formLabel}>统一社会信用代码</label>
                <Input
                  value={form.creditCode}
                  onChange={(e) => updateField("creditCode", e.target.value)}
                  placeholder="18位统一社会信用代码"
                  maxLength={18}
                  className={badgeStyles.modalInput}
                />
              </div>
            )}

            {/* Address */}
            <div className={styles.formGroupFull}>
              <label className={styles.formLabel}>地址</label>
              <Input
                value={form.address}
                onChange={(e) => updateField("address", e.target.value)}
                placeholder="注册地址、办公地址或司法送达地址"
                maxLength={200}
                className={badgeStyles.modalInput}
              />
            </div>

            {/* Contact Name */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>联系人姓名</label>
              <Input
                value={form.contactName}
                onChange={(e) => updateField("contactName", e.target.value)}
                placeholder="如：王经理"
                maxLength={50}
                className={badgeStyles.modalInput}
              />
            </div>

            {/* Contact Phone */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>联系电话</label>
              <Input
                value={form.contactPhone}
                onChange={(e) => updateField("contactPhone", e.target.value)}
                placeholder="如：13800000000"
                maxLength={20}
                className={badgeStyles.modalInput}
              />
            </div>

            {/* Contact Email — full width */}
            <div className={styles.formGroupFull}>
              <label className={styles.formLabel}>联系邮箱</label>
              <Input
                value={form.contactEmail}
                onChange={(e) => updateField("contactEmail", e.target.value)}
                placeholder="输入邮箱地址"
                maxLength={100}
                className={badgeStyles.modalInput}
              />
            </div>

            {/* Notes — full width */}
            <div className={styles.formGroupFull}>
              <label className={styles.formLabel}>备注</label>
              <Input.TextArea
                value={form.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                placeholder="输入备注信息"
                rows={3}
                maxLength={500}
                className={badgeStyles.modalTextarea}
              />
            </div>
          </div>
        </form>
      </Modal>
    </>
  );
}
